import type { AreaStatus, ScanEstimate } from '../../../shared/types';
import { shell } from '../app';
import { ApiError, NetworkError } from '../data/api';
import { estimateArea, getAreas, scanArea } from '../data/daily';
import { areaDetailLines, areaHeadline, scanAsk, scanStopText } from '../logic/daily';
import { btn, empty, linkBtn, select } from '../ui/components';
import { h, replaceChildren } from '../ui/dom';
import { AREA_DETAILS_SUMMARY } from '../ui/labels';
import type { View } from '../ui/router';

/** The areas of south Orange County and how far each is covered (D82); an area is looked up on
 *  Google only after its estimate is shown and he says yes. */


const friendly = (err: unknown): string =>
  err instanceof NetworkError
    ? 'The areas need a connection.'
    : err instanceof ApiError
      ? err.message
      : 'Something went wrong. Try again.';

const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "5 chains · 3 on Square · 1 not a fit · 2 closed" — why the rest are out of the pool. */
const leftOut = (e: AreaStatus['excluded']): string =>
  [
    e.chain ? count(e.chain, 'chain') : '',
    e.square ? `${e.square} on Square` : '',
    e.not_fit ? `${e.not_fit} not a fit` : '',
    e.non_merchant ? `${e.non_merchant} probably not merchants` : '',
    e.closed ? `${e.closed} closed` : '',
  ]
    .filter(Boolean)
    .join(' · ');

export const routeAreasView: View = async ({ root }) => {
  shell.setChrome(true);
  shell.setTitle('Areas', { subtitle: 'Orange County · South OC first, then county roster order' });
  const host = h('div', { class: 'page page--areas' });
  root.appendChild(host);
  let disposed = false;
  let areas: AreaStatus[] = [];
  let busy = false;
  let highlighted = '';
  let resultArea = '';
  let resultMessage = '';
  let timer: ReturnType<typeof setInterval> | undefined;
  const status = h('div', { role: 'status', 'aria-live': 'polite' });
  const list = h('div', { class: 'arealist' });
  const search = h('input', {
    type: 'search', class: 'input input--search',
    placeholder: 'Search areas', 'aria-label': 'Search areas',
  });
  const filter = select({
    ariaLabel: 'Lookup status', value: 'all', onChange: () => render(),
    options: [
      { value: 'all', label: 'All areas' },
      { value: 'yes', label: 'Looked up' },
      { value: 'no', label: 'Not looked up yet' },
    ],
  });
  const elapsed = h('p', { 'aria-live': 'off', hidden: true });
  const showStatus = (message: string, dismiss = true) =>
    replaceChildren(
      status,
      h('p', {}, message),
      dismiss
        ? btn('Dismiss', {
            variant: 'ghost',
            onClick: () => {
              resultMessage = '';
              highlighted = '';
              replaceChildren(status);
              render();
            },
          })
        : null,
    );
  const render = () => {
    if (disposed) return;
    const visible = areas.filter(
      (a) =>
        a.name.toLowerCase().includes(search.value.toLowerCase()) &&
        (filter.value === 'all' || Boolean(a.lookup_complete) === (filter.value === 'yes')),
    );
    replaceChildren(list, ...visible.map(areaRow));
    if (!visible.length) list.append(empty('No areas match these filters.'));
    list.querySelectorAll('button').forEach((b) => {
      b.disabled = busy;
    });
  };
  search.addEventListener('input', render);

  const back = () => linkBtn('Back to the plan', '#/route/plan', { variant: 'ghost' });

  const areaRow = (a: AreaStatus) => {
    const out = leftOut(a.excluded);
    const stop = scanStopText(a);
    return h(
      'div',
      {
        class: 'arearow',
        'data-area': a.name,
        ...(a.name === highlighted
          ? { style: 'outline: 2px solid var(--accent); outline-offset: 2px;' }
          : {}),
      },
      h('div', { class: 'arearow__name' }, a.name),
      h('div', { class: 'arearow__progress' }, areaHeadline(a)),
      h(
        'div',
        { class: 'arearow__out' },
        a.distance_miles != null
          ? `About ${a.distance_miles.toFixed(1)} miles from home · straight-line`
          : 'Distance from home unavailable',
      ),
      a.name === resultArea && resultMessage ? h('p', {}, resultMessage) : null,
      out ? h('div', { class: 'arearow__out' }, `Left out: ${out}`) : null,
      stop ? h('p', {}, stop) : null,
      h('details', { class: 'arearow__more' },
        h('summary', {}, AREA_DETAILS_SUMMARY),
        ...areaDetailLines(a).map((line) => h('p', {}, line))),
      a.lookup_complete ? null : btn(a.state === 'incomplete' ? 'Continue scan' : a.state === 'interrupted' ? 'Recover scan' : a.state === 'stale' || a.state === 'legacy-unknown' ? 'Start over' : 'Look it up', {
        variant: 'subtle',
        size: 'sm',
        onClick: () => void ask(a.name),
      }),
    );
  };

  const load = async () => {
    try {
      const fresh = await getAreas();
      if (!disposed) {
        areas = fresh;
        render();
      }
      return true;
    } catch (err) {
      if (!disposed) {
        showStatus(
          `${resultMessage} Could not refresh the areas: ${friendly(err)} The displayed list may be out of date.`,
        );
        status.append(btn('Retry refresh', { variant: 'subtle', onClick: () => void load() }));
      }
      return false;
    }
  };

  const scan = async (name: string, quote: ScanEstimate) => {
    if (busy || disposed) return;
    busy = true;
    highlighted = '';
    render();
    resultArea = name;
    resultMessage = '';
    const start = Date.now();
    replaceChildren(
      status,
      h('progress', { 'aria-label': `Looking up ${name}` }),
      h('p', {}, `Looking up ${name}…`),
    );
    elapsed.hidden = false;
    const progress = () => {
      elapsed.textContent = `${Math.floor((Date.now() - start) / 1000)} seconds elapsed.`;
    };
    progress();
    timer = setInterval(progress, 1000);
    try {
      const result = await scanArea(name, quote);
      if (disposed) return;
      resultMessage = `${name}: ${result.places} places found.${result.error ? ` Lookup incomplete: ${result.error}` : ''}`;
      showStatus(resultMessage);
    } catch (err) {
      if (disposed) return;
      const running =
        err instanceof ApiError && err.status === 409 && /being looked up|running/i.test(err.message);
      resultMessage = running
        ? `${name}: lookup still running. Check back in a few minutes.`
        : `${name}: ${friendly(err)}${err instanceof NetworkError ? ' The lookup may still be running on the server. Check back before retrying.' : ''}`;
      showStatus(resultMessage, !running);
    } finally {
      elapsed.hidden = true;
      elapsed.textContent = '';
      clearInterval(timer);
      timer = undefined;
      if (!disposed) {
        await load(); // even a disconnected request can have committed server results
        if (!disposed) {
          busy = false;
          highlighted = name;
          search.value = '';
          filter.value = 'all';
          render();
          const changed = [...list.querySelectorAll<HTMLElement>('[data-area]')].find(
            (r) => r.dataset.area === name,
          );
          changed?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
          status.append(btn('Check status', { variant: 'subtle', onClick: () => void load() }));
        }
      }
    }
  };

  const ask = async (name: string) => {
    if (busy || disposed) return;
    busy = true;
    render();
    try {
      const est = await estimateArea(name);
      if (disposed) return;
      const remaining = Math.max(0, est.cap - est.month_calls);
      const confirm = btn('Confirm scan', {
        variant: 'primary', onClick: () => void scan(name, est),
      });
      confirm.disabled = remaining === 0 || est.calls === 0 || (!!est.cooldown_until && Date.parse(est.cooldown_until) > Date.now());
      replaceChildren(
        status,
        h('p', { class: 'plan__ask' }, scanAsk(est)),
        h('p', { class: 'plan__quiet' }, `${est.month_calls} of ${est.cap} lookups used this month.`),
        h(
          'div',
          { class: 'planbar' },
          btn('Not now', { variant: 'ghost', onClick: () => replaceChildren(status) }),
          confirm,
        ),
      );
      status.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    } catch (err) {
      if (!disposed) showStatus(friendly(err));
    } finally {
      busy = false;
      render();
    }
  };

  replaceChildren(host,
    h('div', { class: 'field' }, search), h('div', { class: 'field' }, filter),
    status, elapsed, list, back(),
  );
  void load();
  return () => {
    disposed = true;
    clearInterval(timer);
  };
};
